import {
  useV2ListClientsForAccountQuery,
  useV2ListSalesExecutivesForAccountQuery,
  useV2ListPresalesEngineersForAccountQuery,
  useV2ListTeamsForAccountQuery,
  useV2ListUsersForAccountQuery,
  useV2ListTagsForAccountQuery,
  useV2ListRateTablesForAccountQuery,
  useV2ListLineOfBusinessesForAccountQuery,
  useV2ListBusinessUnitsForAccountQuery,
  useV2GetAccountQuery,
} from "@generated";

export interface FilterOption {
  value: string;
  label: string;
  /** Record id; carried for tags, whose value is the name but whose saved `tagIds` are ids. */
  id?: string;
}
export interface FilterOptionList {
  options: FilterOption[];
  failed: boolean;
}
export interface FilterOptions {
  clients: FilterOptionList;
  salesExecs: FilterOptionList;
  presalesEngineers: FilterOptionList;
  teams: FilterOptionList;
  teammates: FilterOptionList;
  tags: FilterOptionList; // value = tag name (the API filters tag-list by name)
  rateTables: FilterOptionList;
  lobs: FilterOptionList;
  businessUnits: FilterOptionList;
  businessUnitLabel: string;
  statusLabel: (status: string) => string;
}

interface NamedResource {
  id?: string;
  attributes: { name?: string };
}
interface ListQuery {
  data?: { data?: NamedResource[] };
  isError?: boolean;
}

const toList = (q: ListQuery, valueFromLabel = false): FilterOptionList => ({
  failed: !!q.isError,
  options: (q.data?.data ?? [])
    .map((r) => {
      const label = String(r.attributes?.name ?? "");
      return valueFromLabel
        ? { value: label, label, id: r.id != null ? String(r.id) : undefined }
        : { value: String(r.id), label };
    })
    .filter((o) => o.label)
    .sort((a, b) => a.label.localeCompare(b.label)),
});

const titleize = (s: string) =>
  s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export interface TypeAheadTerms {
  clients: string;
  salesExecs: string;
  presalesEngineers: string;
  tags: string;
}
export type TypeAheadResults = Record<keyof TypeAheadTerms, FilterOptionList>;

/** Server-side name search for the four lists legacy searched as you type. Empty term skips the query. */
export function useTypeAheadOptions(terms: TypeAheadTerms): TypeAheadResults {
  const skip = (t: string) => ({ skip: !t });
  const clients = useV2ListClientsForAccountQuery(
    { filter: { name: terms.clients, active: true }, page: { size: 100 } },
    skip(terms.clients)
  );
  const salesExecs = useV2ListSalesExecutivesForAccountQuery(
    { filter: { name: terms.salesExecs, active: true }, page: { size: 100 } },
    skip(terms.salesExecs)
  );
  const presales = useV2ListPresalesEngineersForAccountQuery(
    { filter: { name: terms.presalesEngineers }, page: { size: 100 } },
    skip(terms.presalesEngineers)
  );
  const tags = useV2ListTagsForAccountQuery(
    { filter: { name: terms.tags }, page: { size: 100 } },
    skip(terms.tags)
  );
  return {
    clients: toList(terms.clients ? clients : {}),
    salesExecs: toList(terms.salesExecs ? salesExecs : {}),
    presalesEngineers: toList(terms.presalesEngineers ? presales : {}),
    tags: toList(terms.tags ? tags : {}, true),
  };
}

export function useFilterOptions(): FilterOptions {
  const clients = useV2ListClientsForAccountQuery({ page: { size: 1000 } });
  const salesExecs = useV2ListSalesExecutivesForAccountQuery({ page: { size: 100 } });
  const presales = useV2ListPresalesEngineersForAccountQuery({ page: { size: 100 } });
  const teams = useV2ListTeamsForAccountQuery({ page: { size: 100 } });
  const users = useV2ListUsersForAccountQuery({ page: { size: 500 } });
  const tags = useV2ListTagsForAccountQuery({ page: { size: 250 } });
  const rateTables = useV2ListRateTablesForAccountQuery({ page: { size: 250 } });
  const lobs = useV2ListLineOfBusinessesForAccountQuery({ page: { size: 250 } });
  const businessUnits = useV2ListBusinessUnitsForAccountQuery({ page: { size: 250 } });
  const account = useV2GetAccountQuery({});

  const fieldLabels: Record<string, string | undefined> =
    account.data?.data?.attributes?.["field-labels"] ?? {};

  return {
    clients: toList(clients),
    salesExecs: toList(salesExecs),
    presalesEngineers: toList(presales),
    teams: toList(teams),
    teammates: toList(users),
    tags: toList(tags, true),
    rateTables: toList(rateTables),
    lobs: toList(lobs),
    businessUnits: toList(businessUnits),
    businessUnitLabel: fieldLabels.business_unit || "Business Unit",
    statusLabel: (status: string) => fieldLabels[status] || titleize(status),
  };
}

export default useFilterOptions;
